import { AWS_ARN_REGEX } from "@/common/aws";
import {
	AutoApprovalThreshold,
	EnvironmentClassification,
	EnvironmentProvider,
	EnvironmentStatus,
} from "@/common/model";

/** AWS `sts:ExternalId` allowed charset. */
export const AWS_EXTERNAL_ID_REGEX = /^[\w+=,.@:/-]+$/;

/** AWS account IDs are exactly 12 digits. */
export const AWS_ACCOUNT_ID_REGEX = /^\d{12}$/;

/** Classification choices, shared by the create + edit wizards. */
export const CLASSIFICATION_OPTIONS = [
	{
		value: EnvironmentClassification.NON_PRODUCTION,
		label: "Non-production",
		description: "Development, staging, or testing",
	},
	{
		value: EnvironmentClassification.PRODUCTION,
		label: "Production",
		description: "Live environment serving real users",
	},
];

/**
 * Automation policy choices, shared by the create + edit wizards. One ordered axis: an action runs
 * unattended when its assessed risk sits at or below the chosen level. Pair the selector with
 * `AutomationPolicyLadder` rather than a static risk table — the ladder shows which tiers the
 * selected policy actually covers.
 */
export const THRESHOLD_OPTIONS = [
	{
		value: AutoApprovalThreshold.NONE,
		label: "Human-gated",
		description: "Every agent action waits for a person",
	},
	{
		value: AutoApprovalThreshold.MEDIUM,
		label: "Supervised",
		description: "Agents inspect and make reversible changes on their own",
	},
	{
		value: AutoApprovalThreshold.HIGH,
		label: "Autonomous",
		description:
			"Agents act alone at every risk level except Critical, which always waits for a person",
	},
];

/**
 * Action risk tiers, highest impact first — the order the ladder renders them in, so the most
 * consequential tier is the one you read first.
 */
export const AUTOMATION_RISK_ROWS = [
	{
		key: "critical",
		label: "Critical risk",
		detail: "Irreversible destruction of data or infrastructure",
	},
	{
		key: "high",
		label: "High risk",
		detail: "Destructive but recoverable changes",
	},
	{ key: "medium", label: "Medium risk", detail: "Reversible changes" },
	{ key: "low", label: "Low risk", detail: "Read-only inspection, no changes" },
] as const;

export type AutomationRiskKey = (typeof AUTOMATION_RISK_ROWS)[number]["key"];

/** Rank of each risk tier on the shared severity scale. Mirrors `ApprovalService.RiskRank`. */
const RISK_RANK: Record<AutomationRiskKey, number> = {
	low: 1,
	medium: 2,
	high: 3,
	// Above every threshold rank, mirroring the backend's `int.MaxValue`: no policy reaches it, so
	// Critical is always gated. `AutoApprovalThreshold` has no Critical member by design (ADR-049).
	critical: Number.POSITIVE_INFINITY,
};

/** Rank of each policy on the same scale. Mirrors `ApprovalService.ThresholdRank`. */
const THRESHOLD_RANK: Record<AutoApprovalThreshold, number> = {
	NONE: 0,
	MEDIUM: 2,
	HIGH: 3,
};

/**
 * Whether an action of the given risk tier runs unattended under the given policy. The single
 * source of truth behind `AutomationPolicyLadder`, mirroring the backend's one-line
 * `ShouldAutoApprove`: risk at or below the policy runs, anything above waits.
 *
 * Classification is deliberately not a factor — the risk evaluator already escalates one level for
 * a production target, so counting it here too would turn one axis back into a matrix.
 *
 * Returns false for `critical` under every threshold; see `RISK_RANK`.
 */
export function riskRunsUnattended(
	threshold: AutoApprovalThreshold,
	risk: AutomationRiskKey,
): boolean {
	return RISK_RANK[risk] <= THRESHOLD_RANK[threshold];
}

/** Colored provider logo served from public/assets/icons/<slug>/<slug>-logo.png. */
const providerLogo = (slug: string, disabled = false) => (
	<img
		src={`/assets/icons/${slug}/${slug}-logo.png`}
		alt=""
		style={{
			width: 20,
			height: 20,
			objectFit: "contain",
			filter: disabled ? "grayscale(1)" : undefined,
			opacity: disabled ? 0.5 : undefined,
		}}
	/>
);

/** Selectable providers, shared by the create wizard and the setup dialog.
 *  `slug` doubles as the icon folder name and the docs-site path segment. */
export const PROVIDER_ITEMS = [
	{
		value: EnvironmentProvider.AWS,
		label: "Amazon Web Services",
		slug: "aws",
		icon: providerLogo("aws"),
	},
	{
		value: EnvironmentProvider.AZURE,
		label: "Microsoft Azure",
		slug: "azure",
		icon: providerLogo("azure", true),
		disabled: true,
	},
	{
		value: EnvironmentProvider.GOOGLE_CLOUD,
		label: "Google Cloud",
		slug: "google-cloud",
		icon: providerLogo("google-cloud", true),
		disabled: true,
	},
	{
		value: EnvironmentProvider.CLOUDFLARE,
		label: "Cloudflare",
		slug: "cloudflare",
		icon: providerLogo("cloudflare", true),
		disabled: true,
	},
	{
		value: EnvironmentProvider.VERCEL,
		label: "Vercel",
		slug: "vercel",
		icon: providerLogo("vercel", true),
		disabled: true,
	},
	{
		value: EnvironmentProvider.OTHER,
		label: "On-premises",
		slug: "on-premises",
		icon: providerLogo("on-premises", true),
		disabled: true,
	},
];

/** Docs page describing what's needed to set up an account for this provider. */
export function providerDocsUrl(provider: EnvironmentProvider): string {
	const slug =
		PROVIDER_ITEMS.find((i) => i.value === provider)?.slug ?? "overview";
	return `https://docs.stratonext.ai/environments/${slug}`;
}

/**
 * Ordered metadata field descriptors per provider.
 * Each entry describes a key-value pair that can be collected from the user
 * and stored in `ArtemisEnvironment.Metadata`.
 */
export const PROVIDER_METADATA_FIELDS: Record<
	string,
	Array<{
		key: string;
		label: string;
		placeholder: string;
		required: boolean;
		description: string;
		/** When set, this field is only shown/required while the named group's switch is on. */
		group?: string;
		/**
		 * Write-only secret: rendered as a password input, never displayed after save. The API
		 * stores it in AWS Secrets Manager and returns only a masked marker.
		 */
		secret?: boolean;
		/** Input widget to render. Defaults to a plain text input. */
		type?: "region";
		/** Caps the text input length (plain-text fields only). */
		maxLength?: number;
		/** Regex the value must match; surfaced as a form validation rule. */
		pattern?: RegExp;
		/** Validation message for {@link pattern}; defaults to the AWS-ARN message. */
		patternMessage?: string;
		/**
		 * Marks the single field whose presence means agents can be deployed to this
		 * environment (AWS: the deployment role ARN). Azure/GCP mark their own
		 * equivalent field. Read by {@link environmentCanDeploy}.
		 */
		deploymentIndicator?: boolean;
	}>
> = {
	[EnvironmentProvider.AWS]: [
		{
			key: "AccountId",
			label: "Account ID",
			placeholder: "123456789012",
			required: true,
			description: "The AWS account this environment lives in.",
			pattern: AWS_ACCOUNT_ID_REGEX,
			patternMessage: "Enter a 12-digit AWS account ID",
			maxLength: 12,
		},
		{
			key: "DefaultRegion",
			label: "Default Region",
			placeholder: "us-east-1",
			required: true,
			description: "Default AWS region for this environment.",
			type: "region",
		},
		{
			key: "ConnectionRoleArn",
			label: "Connection Role ARN",
			placeholder: "arn:aws:iam::123456789012:role/ConnectionRole",
			required: true,
			description: "Role with rights to read Cloudformation stacks",
			pattern: AWS_ARN_REGEX,
		},
		{
			key: "AgentOperationsRoleArn",
			label: "Agent Default Operations Role ARN",
			placeholder: "arn:aws:iam::123456789012:role/OperationsRole",
			required: true,
			description:
				"The baseline role an Agent assumes to access the environment. Controls exactly what any agent can read or change by default, without requiring any additional permissions.",
			group: "agentAccess",
			pattern: AWS_ARN_REGEX,
		},
		{
			key: "AgentOperationsMaxRoleArn",
			label: "Agent Operations Max Role ARN",
			placeholder: "arn:aws:iam::123456789012:role/OperationsMaxRole",
			required: false,
			description:
				"Optional. Maximum role: Defines the maximum permissions an agent can have in this environment. The agent cannot access or perform anything beyond what is defined here. Leave empty to disable permission escalation.",
			group: "agentAccess",
			pattern: AWS_ARN_REGEX,
		},
		{
			key: "AgentOperationsExternalId",
			label: "Agent Operations External ID",
			placeholder: "a random secret you choose",
			required: false,
			description:
				"This acts as a shared secret: only requests with the correct External ID can assume the role. You won’t see it again after saving.",
			group: "agentAccess",
			secret: true,
			pattern: AWS_EXTERNAL_ID_REGEX,
			patternMessage: "Use only letters, digits, and + = , . @ : / -",
		},
		// Order within the deployment group matches the CloudFormation stack Outputs (Runtime then
		// Deployment) so pasting the ARNs top-to-bottom lines up.
		{
			key: "AgentRuntimeRoleArn",
			label: "Agent Default Runtime Role ARN",
			placeholder: "arn:aws:iam::123456789012:role/RuntimeRole",
			required: true,
			description:
				"The basic runtime role a deployed agent runs as inside the account. Should provide minimal permissions needed for the agent to function.",
			group: "agentDeployment",
			pattern: AWS_ARN_REGEX,
		},
		{
			key: "AgentDeploymentRoleArn",
			label: "Agent Deployment Role ARN",
			placeholder: "arn:aws:iam::123456789012:role/DeploymentRole",
			required: true,
			description:
				"Lets StratoNext create and remove an agent in this account.",
			group: "agentDeployment",
			pattern: AWS_ARN_REGEX,
			deploymentIndicator: true,
		},
	],
	[EnvironmentProvider.AZURE]: [
		{
			key: "SubscriptionId",
			label: "Subscription ID",
			placeholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
			required: true,
			description: "The Azure subscription this environment lives in.",
		},
		{
			key: "OrganizationId",
			label: "Organization ID",
			placeholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
			required: true,
			description: "The Azure Active Directory tenant for this subscription.",
		},
		{
			key: "ResourceGroup",
			label: "Resource Group",
			placeholder: "my-resource-group",
			required: false,
			description: "Optional, scopes discovery to a single resource group.",
		},
	],
};

/**
 * Toggle groups per provider. Each group gates a set of `PROVIDER_METADATA_FIELDS`
 * entries (matched by `field.group`) behind a single yes/no switch, shown as one
 * extra question in the wizard instead of a wall of "optional" role ARNs.
 */
export const PROVIDER_METADATA_GROUPS: Record<
	string,
	Array<{ key: string; label: string; description: string }>
> = {
	[EnvironmentProvider.AWS]: [
		{
			key: "agentAccess",
			label: "Allow agents to access this environment",
			description:
				"Agents will be able to assume a role to inspect or act on resources here.",
		},
		{
			key: "agentDeployment",
			label: "Allow agents to be deployed to this environment",
			description:
				"StratoNext will be able to deploy and run agents directly into this account.",
		},
	],
};

/**
 * Whether agents can be deployed to this environment: the persisted, user-controlled
 * `AllowAgentsToBeDeployed` flag AND the environment is not disabled. Independent of whether the
 * deployment-role ARN happens to be configured, so a user can keep the ARN but toggle deployment off.
 */
export function environmentCanDeploy(environment: {
	status?: EnvironmentStatus | null;
	allowAgentsToBeDeployed?: boolean | null;
}): boolean {
	return (
		environment.status !== EnvironmentStatus.DISABLED &&
		!!environment.allowAgentsToBeDeployed
	);
}

/**
 * Flat map from metadata key to its human-readable label.
 * Derived from `PROVIDER_METADATA_FIELDS` — use this wherever only the label
 * is needed (e.g. display views) without iterating per-provider.
 *
 * @example
 * METADATA_LABELS["AccountId"]       // "Account ID"
 * METADATA_LABELS["DeploymentRoleArn"] // "Deployment Role ARN"
 */
export const METADATA_LABELS: Record<string, string> = Object.values(
	PROVIDER_METADATA_FIELDS,
).reduce<Record<string, string>>((acc, fields) => {
	for (const field of fields) {
		acc[field.key] = field.label;
	}
	return acc;
}, {});

/**
 * Metadata keys that hold write-only secrets. The API returns only a masked marker for these,
 * so display views should render a "configured" indicator rather than the raw value.
 */
export const SECRET_METADATA_KEYS: ReadonlySet<string> = new Set(
	Object.values(PROVIDER_METADATA_FIELDS)
		.flat()
		.filter((field) => field.secret)
		.map((field) => field.key),
);

// ── AWS onboarding template generation ────────────────────────────────────────
// The frontend generates the External ID and role names, then asks the API to bake them into a
// tailored CloudFormation template. The operator/ceiling action sets are chosen per-environment on the
// wizard's "Role permissions" step — see aws-service-catalog.ts.

/** Base (unsuffixed) StratoNext role names, keyed by role. */
export const AWS_ROLE_BASE_NAMES = {
	connection: "StratoNextConnectionRole",
	operations: "StratoNextOperationsRole",
	operationsMax: "StratoNextOperationsMaxRole",
	runtime: "StratoNextRuntimeRole",
	deployment: "StratoNextDeploymentRole",
} as const;

/** Maps each role to the environment-metadata ARN field it fills, for wizard prefill. */
export const AWS_ROLE_ARN_METADATA_KEYS = {
	connection: "ConnectionRoleArn",
	operations: "AgentOperationsRoleArn",
	operationsMax: "AgentOperationsMaxRoleArn",
	runtime: "AgentRuntimeRoleArn",
	deployment: "AgentDeploymentRoleArn",
} as const;

export type AwsRoleKey = keyof typeof AWS_ROLE_BASE_NAMES;

/** Full role names for a given suffix (e.g. `StratoNextOperationsRole-a1b2c3`). */
export type AwsRoleNames = Record<AwsRoleKey, string>;

export function buildAwsRoleNames(suffix: string): AwsRoleNames {
	return {
		connection: `${AWS_ROLE_BASE_NAMES.connection}-${suffix}`,
		operations: `${AWS_ROLE_BASE_NAMES.operations}-${suffix}`,
		operationsMax: `${AWS_ROLE_BASE_NAMES.operationsMax}-${suffix}`,
		runtime: `${AWS_ROLE_BASE_NAMES.runtime}-${suffix}`,
		deployment: `${AWS_ROLE_BASE_NAMES.deployment}-${suffix}`,
	};
}

/** A fresh External ID (confused-deputy guard baked into every role's trust policy). */
export const generateExternalId = (): string => crypto.randomUUID();

/** A short random suffix that keeps role names unique across environments in one account. */
export const generateRoleSuffix = (): string =>
	crypto.randomUUID().replace(/-/g, "").slice(0, 6);

/** Builds the IAM role ARN the CloudFormation stack will create for `name` in `accountId`. */
export const awsRoleArn = (accountId: string, name: string): string =>
	`arn:aws:iam::${accountId}:role/${name}`;
